/**
 * WebSocket Service for HIVA WebChat Widget
 */

import type { WebSocketMessage, Message } from '../types';

export class WebChatWebSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isIntentionallyClosed = false;

  // Event handlers
  private onMessageCallback: ((message: Message) => void) | null = null;
  private onConnectCallback: (() => void) | null = null;
  private onDisconnectCallback: (() => void) | null = null;
  private onTypingCallback: ((isTyping: boolean) => void) | null = null;

  constructor(url: string) {
    this.url = url;
  }

  /**
   * Connect to WebSocket
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[WebChat] Already connected');
      return;
    }

    this.isIntentionallyClosed = false;
    console.log('[WebChat] Connecting to WebSocket:', this.url);

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('[WebChat] WebSocket connected');
        this.reconnectAttempts = 0;
        this.onConnectCallback?.();
      };

      this.ws.onmessage = (event) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('[WebChat] Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebChat] WebSocket error:', error);
      };

      this.ws.onclose = () => {
        console.log('[WebChat] WebSocket disconnected');
        this.onDisconnectCallback?.();

        // Attempt to reconnect if not intentionally closed
        if (!this.isIntentionallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
          console.log(`[WebChat] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          setTimeout(() => this.connect(), delay);
        }
      };
    } catch (error) {
      console.error('[WebChat] Error creating WebSocket:', error);
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.isIntentionallyClosed = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Send a message through WebSocket
   */
  send(data: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('[WebChat] WebSocket not connected, cannot send message');
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: WebSocketMessage): void {
    console.log('[WebChat] Received message:', data);

    switch (data.type) {
      case 'new_message':
        // Standard format used by backend (WhatsApp, Zammad, Admin Panel)
        if (data.message) {
          const message: Message = {
            id: data.message.id || crypto.randomUUID(),
            conversation_id: data.conversation_id || '',
            content: data.message.content,
            sender_type: data.message.sender_type,
            created_at: data.message.created_at,
            is_read: false,
          };
          this.onMessageCallback?.(message);
        }
        break;

      case 'message':
        // Legacy format - kept for backward compatibility
        if (data.content && data.sender_type && data.created_at) {
          const message: Message = {
            id: data.message_id || crypto.randomUUID(),
            conversation_id: data.conversation_id || '',
            content: data.content,
            sender_type: data.sender_type,
            created_at: data.created_at,
            is_read: false,
          };
          this.onMessageCallback?.(message);
        }
        break;

      case 'typing':
        this.onTypingCallback?.(true);
        // Auto-clear typing indicator after 3 seconds
        setTimeout(() => this.onTypingCallback?.(false), 3000);
        break;

      case 'status':
        // Handle status updates if needed
        console.log('[WebChat] Status update:', data.status);
        break;

      default:
        console.warn('[WebChat] Unknown message type:', data.type);
    }
  }

  /**
   * Register event handlers
   */
  onMessage(callback: (message: Message) => void): void {
    this.onMessageCallback = callback;
  }

  onConnect(callback: () => void): void {
    this.onConnectCallback = callback;
  }

  onDisconnect(callback: () => void): void {
    this.onDisconnectCallback = callback;
  }

  onTyping(callback: (isTyping: boolean) => void): void {
    this.onTypingCallback = callback;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
